import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';

import { getMixpanelToken } from './config';

// Track initialization state
let isInitialized = false;

/**
 * Initialize Mixpanel with autocapture and session recording.
 * No-ops if VITE_MIXPANEL_TOKEN is not set.
 */
export function initMixpanel(): void {
  const token = getMixpanelToken();
  if (!token) {
    return;
  }

  try {
    mixpanel.init(token, {
      autocapture: true,
      track_pageview: 'full-url',
      record_sessions_percent: 100,
    });
    isInitialized = true;
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'mixpanel', action: 'init' },
    });
  }
}

/**
 * Identify user and set userId super property.
 * Call on successful login. People properties are set separately when profile loads.
 */
export function identifyUser(userId: number, email: string): void {
  if (!isInitialized) {
    return;
  }

  try {
    const userIdStr = String(userId);

    // Identify the user
    mixpanel.identify(userIdStr);

    // Set initial people properties (email only, profile data set separately)
    mixpanel.people.set({
      $email: email,
    });

    // Set userId as super property (attached to all events)
    mixpanel.register({ userId: userIdStr });
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'mixpanel', action: 'identify' },
    });
  }
}

/**
 * Update user profile properties in Mixpanel.
 * Call when user profile data becomes available.
 */
export function updateUserProfile(profile: {
  first_name: string;
  last_name: string;
  get_display_name: string;
}): void {
  if (!isInitialized) {
    return;
  }

  try {
    mixpanel.people.set({
      $name: profile.get_display_name,
      firstName: profile.first_name,
      lastName: profile.last_name,
    });
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'mixpanel', action: 'updateProfile' },
    });
  }
}

/**
 * Reset Mixpanel state on logout.
 * Clears distinct_id, super properties, and people properties.
 */
export function resetUser(): void {
  if (!isInitialized) {
    return;
  }

  try {
    mixpanel.reset();
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'mixpanel', action: 'reset' },
    });
  }
}

/**
 * Set or clear org context as super properties.
 * Call when currentOrg changes.
 */
export function setOrgContext(org: { id: number; name: string } | null): void {
  if (!isInitialized) {
    return;
  }

  try {
    if (org) {
      mixpanel.register({
        orgId: String(org.id),
        orgName: org.name,
      });
    } else {
      mixpanel.unregister('orgId');
      mixpanel.unregister('orgName');
    }
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'mixpanel', action: 'setOrgContext' },
    });
  }
}

/**
 * Track an event with optional properties.
 * All events automatically include userId, orgId, and orgName super properties.
 */
export function track(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!isInitialized) {
    return;
  }

  try {
    mixpanel.track(eventName, properties);
  } catch (error) {
    Sentry.captureException(error, {
      tags: { component: 'mixpanel', action: 'track' },
    });
  }
}

/**
 * Check if Mixpanel is initialized (for testing purposes).
 */
export function isMixpanelInitialized(): boolean {
  return isInitialized;
}

/**
 * Reset initialization state (for testing only).
 */
export function _resetForTesting(): void {
  isInitialized = false;
}
